import React, { useState, useCallback } from 'react';
import { formatEther, parseEther, ethers } from 'ethers';
import axios from 'axios';

export const useZwalletTransactions = (ETHERSCAN_API_KEY, ETHERSCAN_BASE_URL) => {
  const [txHistory, setTxHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const getEtherscanUrl = useCallback((module, action, params = {}) => {
    const queryParams = new URLSearchParams({
      module,
      action,
      apikey: ETHERSCAN_API_KEY,
      ...params
    });
    return `${ETHERSCAN_BASE_URL}?${queryParams.toString()}`;
  }, [ETHERSCAN_API_KEY, ETHERSCAN_BASE_URL]);

  const resolveENSName = async (address, provider) => {
    if (!address || !provider) return address;

    try {
      console.log('🔍 Resolving ENS name for:', address);
      const name = await provider.lookupAddress(address);
      if (name) {
        console.log('✅ ENS name resolved:', name);
        return name;
      }
      return address;
    } catch (error) {
      console.warn('❌ ENS resolution failed:', error.message);
      return address;
    }
  };

  const processTransactions = (transactions, address, provider) => {
    console.log('🔍 Processing transactions, count:', transactions.length);

    const baseTransactions = transactions.map(tx => {
      try {
        const timestamp = parseInt(tx.timeStamp) * 1000;
        const value = formatEther(tx.value || '0');
        const gasUsed = '0';

        const isFromAccount = tx.from.toLowerCase() === address.toLowerCase();
        const status = parseInt(tx.txReceiptStatus) === 1 ? 'confirmed' :
                      tx.isError === '1' ? 'failed' : 'pending';

        return {
          hash: tx.hash,
          to: tx.to || '',
          from: tx.from,
          value: value,
          timestamp: timestamp,
          status: status,
          type: isFromAccount ? 'sent' : 'received',
          gasUsed: gasUsed,
          blockNumber: parseInt(tx.blockNumber) || 0,
          confirmations: parseInt(tx.confirmations) || 0,
          gasPrice: tx.gasPrice ? formatEther(tx.gasPrice) : '0',
          nonce: parseInt(tx.nonce) || 0,
          input: tx.input || '0x',
          toName: tx.to || '',
          fromName: tx.from
        };
      } catch (err) {
        console.warn('Error processing transaction:', tx.hash, err);
        return null;
      }
    }).filter(tx => tx !== null);

    console.log(`✅ Processed ${baseTransactions.length} transactions`);

    if (provider) {
      console.log('🔍 Resolving ENS names for transactions...');
      const ensResolutionPromises = baseTransactions.map(async (tx) => {
        try {
          const fromName = await resolveENSName(tx.from, provider);
          const toName = tx.to ? await resolveENSName(tx.to, provider) : 'Contract Creation';

          return {
            ...tx,
            fromName: fromName,
            toName: toName
          };
        } catch (err) {
          console.warn('Failed to resolve ENS for transaction:', tx.hash, err);
          return tx;
        }
      });

      return Promise.all(ensResolutionPromises)
        .then(transactionsWithENS => {
          setTxHistory(transactionsWithENS);
          setIsLoading(false);
          console.log(`✅ ${transactionsWithENS.length} transactions with ENS names loaded`);
          return transactionsWithENS;
        })
        .catch(ensError => {
          console.warn('ENS resolution failed, using base transactions:', ensError);
          setTxHistory(baseTransactions);
          setIsLoading(false);
          return baseTransactions;
        });
    } else {
      setTxHistory(baseTransactions);
      setIsLoading(false);
      return baseTransactions;
    }
  };

  const fetchTransactionHistory = (address, provider) => {
    if (!address) {
      console.warn('No address provided for transaction history');
      return Promise.resolve([]);
    }

    setIsLoading(true);

    const url = getEtherscanUrl('account', 'txlist', {
      address: address,
      startblock: '0',
      endblock: '99999999',
      sort: 'desc',
      page: '1',
      offset: '25'
    });

    console.log('🔍 Etherscan transaction URL:', url.replace(ETHERSCAN_API_KEY, 'HIDDEN'));

    return axios.get(url, { timeout: 10000 })
      .then(response => {
        console.log('🔍 Etherscan transaction API response received');

        if (response.data.status === '1') {
          console.log(`✅ API Success: Found ${response.data.result?.length || 0} transactions`);
          
          if (response.data.result && Array.isArray(response.data.result) && response.data.result.length > 0) {
            return processTransactions(response.data.result, address, provider);
          } else {
            console.log('ℹ️ No transaction history found');
            setTxHistory([]);
            setIsLoading(false);
            return [];
          }
        } else if (response.data.status === '0') {
          const errorMsg = response.data.message || response.data.result || 'Unknown Etherscan API error';
          
          if (response.data.result && Array.isArray(response.data.result) && response.data.result.length > 0) {
            console.warn(`⚠️ Etherscan API returned NOTOK but we have ${response.data.result.length} transactions. Processing anyway. Error: ${errorMsg}`);
            return processTransactions(response.data.result, address, provider);
          } else {
            console.error(`❌ Etherscan API error: ${errorMsg}`);
            
            let userFriendlyError = 'Failed to load transactions: ';
            
            if (errorMsg.includes('rate limit')) {
              userFriendlyError += 'API rate limit exceeded. Please try again in a moment.';
            } else if (errorMsg.includes('Invalid API Key')) {
              userFriendlyError += 'Invalid API configuration.';
            } else if (errorMsg.includes('Invalid address format')) {
              userFriendlyError += 'Invalid wallet address format.';
            } else {
              userFriendlyError += errorMsg;
            }
            
            throw new Error(userFriendlyError);
          }
        } else {
          console.error('❌ Unexpected API response status:', response.data.status);
          throw new Error('Unexpected API response format');
        }
      })
      .catch(err => {
        setIsLoading(false);

        if (err.response && err.response.status === 429) {
          console.warn('Etherscan API rate limit exceeded for transactions');
          throw new Error('API rate limit exceeded. Please try again in a moment.');
        } else if (err.code === 'NETWORK_ERROR' || err.message.includes('Network Error')) {
          console.warn('Network error fetching transactions');
          throw new Error('Network error. Please check your internet connection.');
        } else if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
          console.warn('Request timeout fetching transactions');
          throw new Error('Request timeout. The network is slow, please try again.');
        } else {
          console.error('Failed to fetch transaction history:', err.message);
          throw err;
        }
      });
  };

  const formatAddressWithENS = (addr, ensName = null) => {
    if (!addr) return 'Unknown';

    if (ensName && ensName !== addr) {
      const formatted = `${ensName}`;
      return (
        <span title={`${addr} (${ensName})`}>
          <a
            href={`https://etherscan.io/address/${addr}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'var(--gold)',
              textDecoration: 'none',
              fontFamily: 'monospace',
              fontSize: '12px'
            }}
            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
            title={`View ${ensName} on Etherscan`}
          >
            🌐 {formatted}
          </a>
        </span>
      );
    }

    const formatted = `${addr.slice(0, 6)}...${addr.slice(-4)}`;
    return (
      <a
        href={`https://etherscan.io/address/${addr}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: 'var(--gold)',
          textDecoration: 'none',
          fontFamily: 'monospace',
          fontSize: '12px'
        }}
        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
        title="View on Etherscan"
      >
        {formatted}
      </a>
    );
  };

  const formatTransactionAddress = (tx, addressType = 'from') => {
    if (!tx) return 'Unknown';

    const addr = addressType === 'from' ? tx.from : tx.to;
    const ensName = addressType === 'from' ? tx.fromName : tx.toName;

    return formatAddressWithENS(addr, ensName);
  };

  const sendTransaction = (sendTo, sendAmount, signer, account, provider, ethPrice, setError, setLoading) => {
    if (!sendTo || !sendAmount || !signer) {
      return Promise.reject(new Error('Please fill all required fields'));
    }

    if (!ethers.isAddress(sendTo)) {
      return Promise.reject(new Error('Invalid recipient address format'));
    }

    const amount = parseFloat(sendAmount);
    if (isNaN(amount) || amount <= 0) {
      return Promise.reject(new Error('Please enter a valid amount'));
    }

    setLoading(true);
    setError('');

    return provider.getBalance(account)
      .then(balance => {
        const balanceEth = parseFloat(formatEther(balance));

        if (amount > balanceEth) {
          throw new Error(`Insufficient balance. You have ${balanceEth.toFixed(6)} ETH`);
        }

        if (amount > 1000) {
          throw new Error('Transaction amount too large. Maximum 1000 ETH per transaction.');
        }

        console.log('🔍 Preparing transaction:', {
          to: sendTo,
          value: sendAmount,
          from: account
        });

        return provider.estimateGas({
          to: sendTo,
          value: parseEther(sendAmount),
          from: account
        }).catch(gasError => {
          console.warn('Gas estimation failed, using default:', gasError);
          return 21000n;
        });
      })
      .then(gasEstimate => {
        const gasLimit = gasEstimate * 120n / 100n;

        return signer.sendTransaction({
          to: sendTo,
          value: parseEther(sendAmount),
          gasLimit: gasLimit
        });
      })
      .then(tx => {
        console.log('✅ Transaction sent:', {
          hash: tx.hash,
          to: sendTo,
          value: sendAmount,
          gasLimit: tx.gasLimit.toString()
        });

        const resolveRecipientENS = async () => {
          try {
            return await resolveENSName(sendTo, provider);
          } catch (err) {
            console.warn('Failed to resolve recipient ENS:', err);
            return sendTo;
          }
        };

        return Promise.all([tx, resolveRecipientENS()]);
      })
      .then(([tx, recipientENSName]) => {
        const pendingTx = {
          hash: tx.hash,
          to: sendTo,
          toName: recipientENSName,
          from: account,
          fromName: account,
          value: sendAmount,
          timestamp: Date.now(),
          status: 'pending',
          type: 'sent',
          gasUsed: '0',
          blockNumber: 0,
          confirmations: 0,
          gasPrice: formatEther(tx.gasPrice || '0'),
          nonce: tx.nonce,
          input: '0x'
        };

        setTxHistory(prev => [pendingTx, ...prev]);

        const TIMEOUT = 120000;
        const confirmationPromise = tx.wait();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Transaction confirmation timeout')), TIMEOUT)
        );

        return Promise.race([confirmationPromise, timeoutPromise]);
      })
      .then(receipt => {
        console.log('✅ Transaction confirmed:', receipt);

        setTxHistory(prev =>
          prev.map(tx =>
            tx.hash === receipt.hash
              ? {
                  ...tx,
                  status: 'confirmed',
                  blockNumber: receipt.blockNumber,
                  confirmations: 1,
                  gasUsed: '0'
                }
              : tx
          )
        );

        return {
          success: true,
          hash: receipt.hash,
          receipt: receipt,
          message: `✅ Transaction confirmed! Block #${receipt.blockNumber}`
        };
      })
      .catch(err => {
        setTxHistory(prev => prev.filter(tx => tx.status !== 'pending'));

        let errorMessage = 'Transaction failed: ';

        if (err.message.includes('user rejected') || err.message.includes('denied transaction')) {
          errorMessage += 'You rejected the transaction';
        } else if (err.message.includes('insufficient funds')) {
          errorMessage += 'Insufficient funds for gas';
        } else if (err.message.includes('gas')) {
          errorMessage += 'Gas estimation failed. Please try again.';
        } else if (err.message.includes('timeout')) {
          errorMessage += 'Transaction took too long to confirm. It may still succeed.';
        } else if (err.reason) {
          errorMessage += err.reason;
        } else {
          errorMessage += err.message;
        }

        console.error('❌ Transaction failed:', err);
        throw new Error(errorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const formatHash = (hash) => {
    if (!hash) return 'Unknown';

    const formatted = `${hash.slice(0, 8)}...${hash.slice(-6)}`;
    return (
      <a
        href={`https://etherscan.io/tx/${hash}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: 'var(--gold)',
          textDecoration: 'none',
          fontFamily: 'monospace',
          fontSize: '12px'
        }}
        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
        title="View on Etherscan"
      >
        {formatted}
      </a>
    );
  };

  const formatAddress = (addr) => {
    if (!addr) return 'Unknown';

    const formatted = `${addr.slice(0, 6)}...${addr.slice(-4)}`;
    return (
      <a
        href={`https://etherscan.io/address/${addr}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: 'var(--gold)',
          textDecoration: 'none',
          fontFamily: 'monospace',
          fontSize: '12px'
        }}
        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
        title="View on Etherscan"
      >
        {formatted}
      </a>
    );
  };

  const getTransactionByHash = (hash) => {
    return txHistory.find(tx => tx.hash === hash);
  };

  const getRecentTransactions = (count = 5) => {
    return txHistory.slice(0, count);
  };

  const getTransactionsByType = (type) => {
    return txHistory.filter(tx => tx.type === type);
  };

  const getTransactionsByStatus = (status) => {
    return txHistory.filter(tx => tx.status === status);
  };

  const calculateTransactionTotals = () => {
    const sent = txHistory
      .filter(tx => tx.type === 'sent' && tx.status === 'confirmed')
      .reduce((total, tx) => total + parseFloat(tx.value), 0);

    const received = txHistory
      .filter(tx => tx.type === 'received' && tx.status === 'confirmed')
      .reduce((total, tx) => total + parseFloat(tx.value), 0);

    return { sent, received };
  };

  const clearTransactionHistory = () => {
    setTxHistory([]);
  };

  const refreshTransactionStatus = (txHash, provider) => {
    if (!provider) return Promise.resolve();

    return provider.getTransactionReceipt(txHash)
      .then(receipt => {
        if (receipt) {
          setTxHistory(prev =>
            prev.map(tx =>
              tx.hash === txHash
                ? {
                    ...tx,
                    status: receipt.status === 1 ? 'confirmed' : 'failed',
                    blockNumber: receipt.blockNumber,
                    confirmations: 1,
                    gasUsed: '0'
                  }
                : tx
            )
          );
          return receipt.status === 1 ? 'confirmed' : 'failed';
        }
      })
      .catch(err => {
        console.warn('Failed to refresh transaction status:', err);
      });
  };

  return {
    txHistory,
    isLoading,
    fetchTransactionHistory,
    sendTransaction,
    formatHash,
    formatAddress,
    formatAddressWithENS,
    formatTransactionAddress,
    getTransactionByHash,
    getRecentTransactions,
    getTransactionsByType,
    getTransactionsByStatus,
    calculateTransactionTotals,
    clearTransactionHistory,
    refreshTransactionStatus
  };
};

export default useZwalletTransactions;
